<template>
  <div class="input-field">
    <label class="input-title">
      {{ label }}
    </label>
    <div class="input-box">
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :disabled="disabled"
        class="file-input"
        :class="{ 'file-input-disabled': disabled }"
        @change="handleFileUpload"
      />
      <div class="file-input-display">
        <span
          class="file-text"
          :class="{
            'file-placeholder': !selectedFile && !props.currentFileName,
            'file-name': selectedFile || props.currentFileName,
          }"
        >
          {{ selectedFile ? selectedFile.name : props.currentFileName || placeholder }}
        </span>
        <button v-if="selectedFile" class="clear-file-button" @click.stop="clearFile" type="button">
          ×
        </button>
        <div class="paperclip-icon">
          <img src="@/assets/paperclip-gray.svg" alt="attach" />
        </div>
        <div class="separator"></div>
      </div>
    </div>
    <div v-if="required && !modelValue && !currentFileName" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { validateFile } from '@/utils/fileValidation'
import {
  ALLOWED_FILE_EXTENSIONS_ACCEPT,
  ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE,
  MAX_FILE_SIZE_MB,
  FILE_UPLOAD_MESSAGES,
} from '@/utils/file-constants'

interface Props {
  label: string
  placeholder?: string
  accept?: string
  required?: boolean
  hasError?: boolean
  errorMessage?: string
  modelValue?: File | null
  allowedExtensions?: string[]
  maxSize?: number // in MB
  currentFileName?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Selecciona un archivo para cargar a este grupo',
  accept: ALLOWED_FILE_EXTENSIONS_ACCEPT,
  required: false,
  hasError: false,
  errorMessage: FILE_UPLOAD_MESSAGES.FILE_REQUIRED,
  modelValue: null,
  allowedExtensions: () => [...ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE],
  maxSize: MAX_FILE_SIZE_MB,
  currentFileName: '',
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [file: File | null]
  focus: []
  error: [message: string]
}>()

const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File | null>(props.modelValue)

// Validation using existing fileValidation utility
const validateFileWithError = (file: File): { valid: boolean; error?: string } => {
  try {
    const maxSizeBytes = (props.maxSize || MAX_FILE_SIZE_MB) * 1024 * 1024 // Convert MB to bytes
    const allowedExtensions = props.allowedExtensions || [...ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE]

    validateFile(file, maxSizeBytes, allowedExtensions)
    return { valid: true }
  } catch (error) {
    return { valid: false, error: error instanceof Error ? error.message : 'Error de validación' }
  }
}

const handleFileUpload = (event: Event) => {
  if (props.disabled) {
    return
  }

  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]

    // Validate file using existing utility
    const validation = validateFileWithError(file)
    if (!validation.valid) {
      emit('error', validation.error || 'Error de validación')
      target.value = '' // Clear the input
      return
    }

    // File is valid
    selectedFile.value = file
    emit('update:modelValue', selectedFile.value)
    emit('focus')
  }
}

const clearFile = () => {
  if (props.disabled) {
    return
  }

  selectedFile.value = null
  emit('update:modelValue', null)
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    selectedFile.value = newValue
  },
)
</script>

<style scoped>
.input-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 6px;
  width: 100%;
  min-height: 80px;
}

.input-title {
  width: 100%;
  height: 22px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
}

.input-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 2px;
  width: 100%;
  min-height: 50px;
  position: relative;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 48px;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.file-input:disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.file-input-disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.file-input-display {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 48px;
  background: #f2f2f2;
  border-radius: 16px;
  display: flex;
  align-items: center;
  padding: 0 40px 0 16px;
  pointer-events: none;
}

.input-field:has(.file-input:disabled) .file-input-display {
  opacity: 0.6;
  background: #e8e8e8;
}

.file-text {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 80px);
}

.file-placeholder {
  color: #a1a1a1;
}

.file-name {
  color: #2b2a2a;
}

.clear-file-button {
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: #6b6b6b;
  color: #ffffff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  transition: background-color 0.2s;
  z-index: 3;
  pointer-events: auto;
}

.clear-file-button:hover {
  background: #ff0000;
}

.paperclip-icon {
  position: absolute;
  width: 24px;
  height: 24px;
  right: 8px;
  top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.paperclip-icon img {
  width: 18px;
  height: 18px;
}

.separator {
  position: absolute;
  width: 2px;
  height: 24px;
  right: 40px;
  top: 12px;
  background: #a1a1a1;
}

.error-message {
  width: 100%;
  height: 17px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 130%;
  color: #ff0000;
}
</style>
